import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Pagination, Row, Table, Tooltip } from "antd";
import moment from "moment";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import { useAppDispatch, useAppSelector } from "../..";

import { ReactComponent as ArrowDetailsIcon } from "../../img/arrow-details.svg";
import { ReactComponent as RebaseIcon } from "../../img/rebase-icon.svg";
import { ReactComponent as DeleteIcon } from "../../img/delete.svg";
import { expandedTableEyeIcon } from "../../lib/icons";
import { ReactComponent as VersionsHiddenIcon } from "../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../img/versions_icons/versionsShown_icon.svg";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";

import { SearchFilterRoutingOrders } from "./SearchFilterRoutingOrders";

import {
  fetchDeleteRoutingOrder,
  fetchRousingOrderSearch,
  fetchRoutingOrderVersions,
  fetchUpdateRoutingOrderStatus,
} from "../../store/slices/routingOrders/routingOrders";
import {
  IRoutingOrder,
  IRoutingOrderDetails,
  IRoutingOrderVersion,
  IRoutingOrderVersionsList,
} from "../../store/slices/routingOrders/types/routingOrder";

import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { FormFieldValue } from "../../lib/interfaces/FormFieldValue";
import TablePageSelect from "../TablePageSelect/TablePageSelect";
import "./RoutingOrders.less";
import { toHttpFormat } from "../../lib/utils/formatDate";
import { checkIsLoading } from "../../lib/utils";
import { geDicValueById } from "../../lib/utils/versionsTableUtils";
import loadingStatusCodes from "../../lib/const/loadingStatusCodes";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";

interface ITableDataRouteDetails extends IRoutingOrderDetails {
  key: number;
}

interface IModal {
  flag: string;
  payload: AnyObject;
}

const pageSizeOptions = {
  10: 10,
  20: 20,
  50: 50,
  100: 100,
};

const initialStateFilters = {
  toDate: moment(),
  fromDate: moment().subtract(1, "M"),
  orderStatus: [1, 2],
  page: 0,
  size: 10,
  currencyId: 4,
};

const columnsWidths = {
  id: "10%",
  destination: "250px",
  abbrev: "10%",
  offeringName: "",
  categoryId: "10%",
  orderStatusName: "15%",
  datePeriod: "250px",
  actions: "130px",
  buttons: "50px",
};

const columnRoutingOrdersDetailsWidth = {
  proportionRatio: "10%",
  organization: "250px",
  offeringName: "",
  rateMax: "10%",
  currencyName: "15%",
  actions: "430px",
};

export const RoutingOrders = ({
  dicCurrencies,
  offerList,
  dicRoutingOrdersStatuses,
  destinationCategories,
}) => {
  const dispatch = useAppDispatch();
  const [advancedSearchForm] = Form.useForm();
  const {
    routingOrdersList,
    routingOrdersListLoading,
    routingOrdersVersions,
    routingOrdersTotalElements,
    routingOrderUpdated,
    deteleRoutingOrderLoading,
  } = useAppSelector((state) => state.routingOrders);
  const [dataSource, setDataSource] = useState<IRoutingOrder[]>([]);
  const [dataSourceVersions, setDataSourceVersions] = useState<IRoutingOrderVersionsList[]>([]);
  const [advancedSearchFormValues, setAdvancedSearchFormValues] = useState<
    FormFieldValue[] | undefined
  >();
  const [modal, setModal] = useState<IModal>({ flag: "", payload: {} });
  const [filters, setFilters] = useState(initialStateFilters);
  const [objectHistory, setObjectHistory] = useState<IRoutingOrder | undefined>(undefined);
  const [objectVersionHistory, setObjectVersionHistory] = useState<
    IRoutingOrderVersion | undefined
  >(undefined);

  const showHistoryModal = (record: IRoutingOrder) => {
    setObjectHistory(record);
  };

  const showVersionHistoryModal = (record: IRoutingOrderVersion) => {
    setObjectVersionHistory(record);
  };

  // useEffect(() => {
  //   loadRoutingOrdersList(initialStateFilters)
  // }, [])

  useEffect(() => {
    setDataSource(
      getRoutingOrdersByDto(routingOrdersList, dicRoutingOrdersStatuses, dicCurrencies)
    );
  }, [routingOrdersList]);

  const getRoutingOrdersByDto = (
    routingOrdersList: IRoutingOrder[],
    dicRoutingOrdersStatuses: AnyObject[],
    dicCurrencies: AnyObject[]
  ) => {
    const _routingOrdersList = routingOrdersList?.map((item) => {
      const _routingItems = item.routingItems?.map((elem) => {
        return { ...elem, currencyName: geDicValueById(dicCurrencies, elem.currencyId)?.name };
      });
      const _item = { ...item, routingItems: _routingItems };
      return {
        ..._item,
        orderStatusName: geDicValueById(dicRoutingOrdersStatuses, item.orderStatusId)?.name,
      };
    });
    return _routingOrdersList;
  };

  useEffect(() => {
    console.log("routingOrdersVersions", routingOrdersVersions);

    setDataSourceVersions((prev) => {
      const _routingOrdersVersions: IRoutingOrderVersionsList[] = [...prev];
      routingOrdersVersions.forEach((item) => {
        let exist = false;
        prev.forEach((elem) => {
          if (item.id === elem.id) exist = true;
        });
        if (!exist) {
          _routingOrdersVersions.push({
            id: item.id,
            list: item.list.map((version) => {
              return {
                ...version,
                orderStatusName: getOrderStatusNameById(
                  version.orderStatusId,
                  dicRoutingOrdersStatuses
                ),
              };
            }),
          });
        }
      });
      return _routingOrdersVersions;
    });
  }, [routingOrdersVersions]);

  useEffect(() => {
    loadRoutingOrdersList(filters);
  }, [filters]);

  useEffect(() => {
    if (routingOrderUpdated) {
      setDataSource((prev) => {
        const _dataSource: IRoutingOrder[] = prev.map((item) => {
          if (item.id === routingOrderUpdated.id) {
            item.orderStatusId = routingOrderUpdated.orderStatusId;
            item.orderStatusName = getOrderStatusNameById(
              routingOrderUpdated.orderStatusId,
              dicRoutingOrdersStatuses
            );
            item.datePeriod = `${routingOrderUpdated.fromDate} - ${routingOrderUpdated.toDate}`;
            item.isOpenDetails = false;
            item.isOpenVersions = false;
          }
          return item;
        });
        return _dataSource;
      });
    }
  }, [routingOrderUpdated]);

  useEffect(() => {
    if (deteleRoutingOrderLoading === loadingStatusCodes.fulfilled) {
      loadRoutingOrdersList(filters);
    }
  }, [deteleRoutingOrderLoading]);

  const loadRoutingOrdersList = async (filters) => {
    console.log("loadRoutingOrdersList", filters);

    const _filters = { ...filters };
    if (filters?.fromDate) {
      const fromDate = _filters.fromDate
        .set("hour", 0)
        .set("minute", 0)
        .set("second", 0)
        .set("millisecond", 0);
      _filters.fromDate = toHttpFormat(fromDate);
    }
    if (filters?.toDate) {
      const toDate = _filters.toDate
        .set("hour", 23)
        .set("minute", 59)
        .set("second", 59)
        .set("millisecond", 999);
      _filters.toDate = toHttpFormat(filters.toDate);
    }
    if (filters?.orderStatus) {
      _filters.orderStatus = _filters.orderStatus.join(",");
    }
    if (filters?.offeringId) {
      _filters.offeringId = _filters.offeringId.join(",");
    }
    console.log("_filters", _filters);

    await dispatch(fetchRousingOrderSearch({ ..._filters }));
  };

  const getOrderStatusNameById = (orderStatusId: number, dicRoutingOrdersStatuses: any[]) => {
    return dicRoutingOrdersStatuses.find((item) => item.id === orderStatusId)?.name || "";
  };

  const handleToggleDetails = (dataIndex, record: IRoutingOrder) => {
    setDataSource((prev) => {
      const _dataSource = prev.map((item) => {
        if (item.key === record.key) {
          return { ...item, isOpenDetails: !item.isOpenDetails };
        }
        return item;
      });
      return _dataSource;
    });
  };

  const handleToggleVersion = async (record: IRoutingOrder) => {
    await dispatch(
      fetchRoutingOrderVersions({
        routingOrderId: record.id,
      })
    );
    setDataSource((prev) => {
      const _dataSource = prev.map((item) => {
        if (item.key === record.key) {
          return { ...item, isOpenVersions: !item.isOpenVersions };
        }
        return item;
      });
      return _dataSource;
    });
  };

  const [columns, setColumns] = useState([
    {
      title: "Заказ",
      dataIndex: "id",
      key: "id",
      hidden: false,
      width: columnsWidths.id,
    },
    {
      title: "Направление",
      dataIndex: "destination",
      key: "destination",
      hidden: false,
      width: columnsWidths.destination,
      render: (dataIndex, record: IRoutingOrder) => {
        return (
          <div style={{ display: "flex" }}>
            <span style={{ paddingRight: "5px" }}>{record.destination}</span>
            <span>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDetails(dataIndex, record);
                }}
                className={
                  record.isOpenDetails
                    ? "button-icon iconButton routingOrder-icon routingOrder-icon__rotate"
                    : "button-icon iconButton routingOrder-icon"
                }
                style={{ outline: "none", cursor: "pointer" }}
                icon={<ArrowDetailsIcon />}
              />
            </span>
          </div>
        );
      },
    },
    {
      title: "Аббревиатура",
      key: "abbrev",
      dataIndex: "abbrev",
      hidden: true,
      width: columnsWidths.abbrev,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      hidden: false,
      width: columnsWidths.offeringName,
    },
    {
      title: "Категория",
      key: "categoryId",
      dataIndex: "categoryId",
      hidden: false,
      width: columnsWidths.categoryId,
      render: (_, record) => {
        let category = "";
        Array.isArray(destinationCategories) &&
          destinationCategories.forEach((item) => {
            if (item.id === record.categoryId) {
              category = item.name;
            }
          });
        return category;
      },
    },
    {
      title: "Статус",
      key: "orderStatusName",
      dataIndex: "orderStatusName",
      hidden: false,
      width: columnsWidths.orderStatusName,
    },
    {
      title: "Период",
      key: "datePeriod",
      dataIndex: "datePeriod",
      hidden: false,
      width: columnsWidths.datePeriod,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      render: (value, record: IRoutingOrder) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(record);
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ]);

  const [columnDetail, setColumnDetail] = useState([
    {
      title: "%",
      key: "proportionRatio",
      dataIndex: "proportionRatio",
      width: columnRoutingOrdersDetailsWidth.proportionRatio,
    },
    {
      title: "Поставщик",
      key: "organization",
      dataIndex: "organization",
      width: columnRoutingOrdersDetailsWidth.organization,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      width: columnRoutingOrdersDetailsWidth.offeringName,
    },
    {
      title: "Цена",
      key: "rateMax",
      dataIndex: "rateMax",
      width: columnRoutingOrdersDetailsWidth.rateMax,
    },
    {
      title: "Валюта",
      key: "currencyName",
      dataIndex: "currencyName",
      width: columnRoutingOrdersDetailsWidth.currencyName,
    },
    {
      dataIndex: "actions",
      key: "actions",
      hidden: false,
      width: columnRoutingOrdersDetailsWidth.actions,
      render: () => {
        return <div />;
      },
    },
  ]);

  const [columnVersions, setColumnVersions] = useState([
    {
      title: "Заказ",
      dataIndex: "id",
      key: "id",
      hidden: false,
      width: columnsWidths.id,
    },
    {
      title: "Направление",
      dataIndex: "destination",
      key: "destination",
      hidden: false,
      width: columnsWidths.destination,
    },
    {
      title: "Аббревиатура",
      key: "abbrev",
      dataIndex: "abbrev",
      hidden: true,
      width: columnsWidths.abbrev,
    },
    {
      title: "Продукт",
      key: "offeringName",
      dataIndex: "offeringName",
      hidden: false,
      width: columnsWidths.offeringName,
    },
    {
      title: "Категория",
      key: "categoryId",
      dataIndex: "categoryId",
      hidden: false,
      width: columnsWidths.categoryId,
      render: (_, record) => {
        let category = "";
        Array.isArray(destinationCategories) &&
          destinationCategories.forEach((item) => {
            if (item.id === record.categoryId) {
              category = item.name;
            }
          });
        return category;
      },
    },
    {
      title: "Статус",
      key: "orderStatusName",
      dataIndex: "orderStatusName",
      hidden: false,
      width: columnsWidths.orderStatusName,
    },
    {
      title: "Период",
      key: "datePeriod",
      dataIndex: "datePeriod",
      hidden: false,
      width: columnsWidths.datePeriod,
    },
    {
      dataIndex: "actions",
      key: "actions",
      hidden: false,
      width: columnsWidths.actions,
      // render: () => <div style={{width: '100%'}}>actions</div>
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      className: "history-column",
      style: { display: "flex", justifyContent: "end" },
      hidden: false,
      render: (value, record: IRoutingOrderVersion) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showVersionHistoryModal(record);
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ]);

  useEffect(() => {
    console.log("useEffect", columns);
    setColumnVersions((prev) => {
      return prev.map((item) => {
        columns.forEach((column) => {
          if (item.dataIndex === column.dataIndex) {
            item.hidden = column.hidden || false;
          }
        });
        return item;
      });
    });
  }, [columns]);

  const [dataSourceDetail, setDataSourceDetail] = useState<ITableDataRouteDetails[]>([]);

  const expandedRowRender = (record: IRoutingOrder, idx) => {
    return (
      <>
        {record.isOpenDetails && (
          <Table
            columns={columnDetail}
            dataSource={getDetailsTableData(record)}
            pagination={false}
            className="table_no-hover child-table"
            // style={{marginLeft: '-8px', marginRight: '-8px'}}
          />
        )}
        {record.isOpenVersions && (
          <Table
            columns={[...columnVersions].filter((item) => !item.hidden)}
            // columns={getColumnVersion(columns, columnVersions)}
            dataSource={getTableDataVersions(dataSourceVersions, record)}
            rowClassName={getVersionsRowClassName}
            pagination={false}
            showHeader={false}
            className="table_no-hover child-table"
          />
        )}
      </>
    );
  };

  const getColumnVersion = (columns, columnVersions) => {
    const listHideColumns = columns.filter((item) => !item.hidden).map((item) => item.dataIndex);

    return columnVersions.filter((item) => listHideColumns.includes(item.dataIndex));
  };

  const getVersionsRowClassName = (record, idx) => {
    const classNames = ["routingOrder-version-row"];
    if (idx === 0) {
      classNames.push("routingOrder-version-row__active");
    }
    return classNames.join(" ");
  };

  const getTableDataVersions = (
    dataSourceVersions: IRoutingOrderVersionsList[],
    record: IRoutingOrder
  ): IRoutingOrderVersion[] => {
    const _dataSource: IRoutingOrderVersion[] = [];
    dataSourceVersions.forEach((item) => {
      if (item.id === record.id) {
        item.list.forEach((elem, idx) => {
          _dataSource.push({
            ...elem,
            key: idx,
          });
        });
      }
    });
    return _dataSource;
  };

  const getDetailsTableData = (record: IRoutingOrder): IRoutingOrderDetails[] => {
    return record.routingItems;
  };

  const getExpandedRowKeys = (dataSource: IRoutingOrder[]) => {
    const keys: number[] = [];
    if (Array.isArray(dataSource)) {
      dataSource.forEach((item) => {
        if (item.isOpenDetails || item.isOpenVersions) {
          keys.push(item.key);
        }
      });
    }
    return keys;
  };

  const hangleChangePage = async ({ page }: { page: number }) => {
    setFilters((prev) => {
      return { ...prev, page };
    });

    loadRoutingOrdersList(filters);
  };

  const handleSendOrder = (payload) => {
    dispatch(
      fetchUpdateRoutingOrderStatus({
        routingOrderId: payload.id,
        orderStatusId: payload.orderStatusId + 1,
      })
    );
    setModal({ flag: "", payload: {} });
  };

  const handleDeleteOrder = (payload) => {
    dispatch(fetchDeleteRoutingOrder(payload.id));
    setModal({ flag: "", payload: {} });
  };

  const getRowClassName = (record: IRoutingOrder, idx) => {
    if (record.isOpenDetails || record.isOpenVersions) {
      return "routingOrder-version-row routingOrder-version-row__extended";
    }
    return "";
  };

  const getButtonTitleByStatusId = (statusId) => {
    switch (statusId) {
      case 1:
        return "Отправить в обработку";
      case 2:
        return "Исполнить";
      default:
        return "";
    }
  };

  const getRenderButtonSend = (record) => {
    if (record.orderStatusId === 3) return false;
    if (moment(record.toDate).isBefore(moment())) return false;
    return true;
  };
  const getRenderButtonDelete = (record) => {
    if ([3].includes(record.orderStatusId)) return false;
    if (moment(record.toDate).isBefore(moment())) return false;
    return true;
  };

  console.log("dataSource", dataSource);

  return (
    <div className="container_height">
      <div className="routingOrder">
        <Row justify="space-between" align="middle">
          <h1 className="main-title">Список заказов</h1>
        </Row>
        <SearchFilterRoutingOrders
          initialStateFilters={initialStateFilters}
          dicCurrencies={dicCurrencies}
          offerList={offerList}
          dicRoutingOrdersStatuses={dicRoutingOrdersStatuses}
          advancedSearchForm={advancedSearchForm}
          advancedSearchFormValues={advancedSearchFormValues}
          setFilters={setFilters}
          loadRoutingOrdersList={loadRoutingOrdersList}
        />
      </div>

      <div className="routingOrder-table versions-table">
        <Row justify="space-between" align="middle" style={{ paddingBottom: 10 }}>
          <Col span={8} />
          <Col span={8} style={{ textAlign: "center" }}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Pagination
              size="small"
              current={filters.page + 1}
              total={routingOrdersTotalElements}
              showSizeChanger={false}
              onChange={async (page: number) => {
                setFilters((prev) => {
                  return { ...prev, page: page - 1 };
                });
              }}
              pageSize={filters.size}
            />
          </Col>
          <Col span={8} style={{ display: "flex", justifyContent: "flex-end" }}>
            {/* {isToggleableColumns && !isTableLoading && ( */}
            <TablePageSelect
              onChange={(e) =>
                setFilters((prev) => {
                  return { ...prev, size: e, page: initialStateFilters.page };
                })
              }
              value={filters.size}
              pageSizeOptions={pageSizeOptions}
            />

            {/* )} */}
          </Col>
        </Row>

        <Row style={{ display: "flex", justifyContent: "flex-end" }}>
          <ToggleColumns columns={columns} setColumns={setColumns} tableName="routing-orders" />
        </Row>

        <Table
          size="small"
          className="table_no-hover"
          columns={[...columns].filter((item) => !item.hidden)}
          dataSource={dataSource}
          loading={checkIsLoading(routingOrdersListLoading)}
          pagination={false}
          // scroll={{ y: 400, x: 1200 }}
          rowClassName={(row, idx) => getRowClassName(row, idx)}
          expandable={{
            expandedRowKeys: getExpandedRowKeys(dataSource),
            expandedRowRender,
            expandIconColumnIndex: [...columns].filter((item) => !item.hidden).length - 1,
            columnWidth: "130px",
            indentSize: 0,
            expandIcon: ({ expanded, record }) => {
              return (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span className="routingOrder-deteleIcon-wrap">
                    <Tooltip title="Удалить заказ">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setModal({ flag: "delete-open", payload: record });
                        }}
                        className="button-icon iconButton"
                        style={{
                          outline: "none",
                          cursor: "pointer",
                          visibility: getRenderButtonDelete(record) ? "inherit" : "hidden",
                        }}
                        icon={<DeleteIcon />}
                      />
                    </Tooltip>
                  </span>

                  <Tooltip title={getButtonTitleByStatusId(record.orderStatusId)}>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setModal({ flag: "rehandle-open", payload: record });
                      }}
                      className="button-icon iconButton"
                      style={{
                        outline: "none",
                        cursor: "pointer",
                        visibility: getRenderButtonSend(record) ? "inherit" : "hidden",
                      }}
                      icon={<RebaseIcon />}
                    />
                  </Tooltip>

                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleVersion(record);
                    }}
                    className="button-icon iconButton"
                    style={{ outline: "none", cursor: "pointer" }}
                    icon={expandedTableEyeIcon(record.isOpenVersions ? "#0E5298" : "#86ACD2")}
                  />
                </div>
              );
            },
          }}
        />
      </div>

      <Modal
        title="Вы действительно хотите удалить заказ?"
        visible={modal.flag === "delete-open"}
        onCancel={() => setModal({ flag: "", payload: {} })}
        centered
        footer={[
          <Button key="1" onClick={() => setModal({ flag: "", payload: {} })}>
            Нет
          </Button>,
          <Button key="2" type="primary" onClick={() => handleDeleteOrder(modal.payload)}>
            Да
          </Button>,
        ]}
      />

      <Modal
        title={
          modal?.payload?.orderStatusId === 2
            ? "Отметить заказ исполненным?"
            : "Отправить заказ в обработку?"
        }
        visible={modal.flag === "rehandle-open"}
        onCancel={() => setModal({ flag: "", payload: {} })}
        centered
        footer={[
          <Button key="1" onClick={() => setModal({ flag: "", payload: {} })}>
            Нет
          </Button>,
          <Button key="2" type="primary" onClick={() => handleSendOrder(modal.payload)}>
            Да
          </Button>,
        ]}
      />

      <HistoryAndPropertyModal
        objectTypeSysname="tc_routing_order"
        objectId={objectHistory ? objectHistory.id : -1}
        handleHideModal={() => setObjectHistory(undefined)}
        visibleModal={!!objectHistory}
        dateObjectFromForCalendar={objectHistory ? objectHistory.fromDate : ""}
        dateObjectToForCalendar={objectHistory ? objectHistory.toDate : ""}
        hasAccessToEdit={false} // TODO добавить доспы к редактированию
      />

      <HistoryAndPropertyModal
        objectTypeSysname="tc_routing_order"
        objectId={objectVersionHistory ? objectVersionHistory.id : -1}
        handleHideModal={() => setObjectVersionHistory(undefined)}
        visibleModal={!!objectVersionHistory}
        dateObjectFromForCalendar={objectVersionHistory ? objectVersionHistory.fromDate : ""}
        dateObjectToForCalendar={objectVersionHistory ? objectVersionHistory.toDate : ""}
        hasAccessToEdit={false} // TODO добавить доспы к редактированию
      />
    </div>
  );
};
